// src/types/Service.ts

export const PREDEFINED_SERVICE_TYPES = [
  'Oil Change',
  'Tire Change',
  'Brake Service',
  'General Service',
  'Battery Check',
  'Engine Service',
] as const;

export const DUE_SOON_THRESHOLD_DAYS = 7;

export type IntervalUnit = 'days' | 'weeks' | 'months';

export function toIntervalDays(value: number, unit: IntervalUnit): number {
  if (unit === 'days') return value;
  if (unit === 'weeks') return value * 7;
  return value * 30; // months: fixed 30-day month, intentionally simple
}

export interface ServiceSchedule {
  id?: string;
  userId: string;
  truckId: string;
  truckPlateNumber: string;
  serviceType: string;
  isCustomType: boolean;
  intervalDays: number;
  lifecycleStatus: 'active' | 'paused';
  nextDueDate: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface ServiceRecord {
  id?: string;
  userId: string;
  truckId: string;
  serviceScheduleId: string;
  serviceType: string;
  performedDate: Date;
  cost?: number;
  notes?: string;
  wasUnexpected: boolean;
  userMarkedUnexpected: boolean;
  createdAt: Date;
}

// Used only for display — never stored on the document
export type DueDateStatus = 'overdue' | 'due-soon' | 'ok' | 'paused';

export function computeDueDateStatus(schedule: ServiceSchedule): DueDateStatus {
  if (schedule.lifecycleStatus === 'paused') return 'paused';
  const msPerDay = 1000 * 60 * 60 * 24;
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const dueMidnight = new Date(schedule.nextDueDate);
  dueMidnight.setHours(0, 0, 0, 0);
  const daysUntilDue = Math.round(
    (dueMidnight.getTime() - todayMidnight.getTime()) / msPerDay,
  );
  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= DUE_SOON_THRESHOLD_DAYS) return 'due-soon';
  return 'ok';
}

export interface CreateServiceScheduleData {
  truckId: string;
  truckPlateNumber: string;
  serviceType: string;
  isCustomType: boolean;
  intervalDays: number;
  nextDueDate: Date;
}

export interface CreateServiceRecordData {
  truckId: string;
  serviceScheduleId: string;
  serviceType: string;
  performedDate: Date;
  cost?: number;
  notes?: string;
  userMarkedUnexpected: boolean;
  newNextDueDate: Date;          // what to write to the schedule's nextDueDate after recording
  intervalDays: number;          // needed to compute wasUnexpected
  currentNextDueDate: Date;      // needed to compute wasUnexpected (early-date check)
}

export interface ServiceSummaryByTruck {
  [truckId: string]: { overdue: number; dueSoon: number };
}
